import React, { useState, useEffect, useMemo, useRef } from 'react';
import ragService from '../../services/rag.service';
import { trackOnboarding } from '../../services/onboardingTelemetry';
import { useNavigate, useParams } from 'react-router-dom';
import { LightBulbIcon } from '@heroicons/react/24/outline';

const Upload = ({ initialCollectionId = '', embedded = false, onComplete, resolveCollection }) => {
  const [file, setFile] = useState(null);
  const [collectionId, setCollectionId] = useState('');
  const [collections, setCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [warning, setWarning] = useState(null); // silent-failure: uploaded but 0/low chunks
  const [backupWarning, setBackupWarning] = useState(null); // durable-backup activation failed (content still searchable)
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const { tenantId } = useParams();

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const response = await ragService.getCollections();
      console.log('✅ Collections:', response);
      console.log('✅ Collections array:', response.collections);
      const available = response.collections || [];
      setCollections(available);
      if (initialCollectionId && available.some((item) => String(item.id) === String(initialCollectionId))) {
        setCollectionId(String(initialCollectionId));
      }
    } catch (err) {
      console.error('❌ Failed to load collections', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!collectionId && !resolveCollection) {
      setError('Please select a collection');
      return;
    }

    const selectedCollection = collections.find(c => String(c.id) === collectionId);
    console.log('📤 Uploading to collection:', {
      id: collectionId,
      name: selectedCollection?.name,
      collection: selectedCollection
    });

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccessMessage('');
    setWarning(null);
    setBackupWarning(null);

    // Create new AbortController for this upload
    abortControllerRef.current = new AbortController();

    try {
      // Collection ID is now a UUID string
      console.log('📤 Sending request with collection ID:', collectionId);
      
      // Setup creates its collection now that there is something to put in it.
      const targetId = resolveCollection ? String((await resolveCollection()).id) : collectionId;
      const response = await ragService.uploadDocument(targetId, file, {
        signal: abortControllerRef.current.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      console.log('✅ Upload response:', response);
      const health = response.document?.ingest_health;
      const chunks = response.document?.chunks || 0;
      if (health && health.sufficient === false) {
        // Thin content now continues to setup's readiness decision, so only
        // a file that produced nothing searchable is a rejection.
        trackOnboarding(chunks > 0 ? 'knowledge_limited' : 'knowledge_rejected', {
          method: 'upload',
          verdict: health.verdict,
          chunks,
          file_type: (file?.name || '').split('.').pop().toLowerCase(),
        });
        // Silent-failure guard: don't show a green "uploaded successfully" when
        // the file produced no/too-little searchable content.
        setWarning({
          verdict: health.verdict,
          reason: health.reason || 'This file produced little or no searchable content.',
          recommendation: health.recommendation || '',
          chunks,
        });
        setSuccessMessage('');
      } else {
        setWarning(null);
        setSuccessMessage(`Document uploaded to "${response.document?.collection}" — ${chunks} chunks created.`);
      }
      // Independent of ingest_health: content indexed fine, but its durable
      // backup didn't activate. Non-blocking — the upload still succeeded.
      setBackupWarning(response.document?.durable_backup_warning || null);
      setFile(null);
      if (onComplete && chunks > 0) {
        onComplete({ response, collection: selectedCollection });
      }
      
      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
      
    } catch (err) {
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        console.log('⏹️ Upload cancelled by user');
        setError('Upload cancelled');
      } else {
        console.error('❌ Upload failed', err);
        const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
        setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      abortControllerRef.current = null;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredCollections = useMemo(() => {
    const q = collectionSearch.trim().toLowerCase();
    if (!q) return collections;

    return collections.filter((collection) => {
      const haystack = [
        collection.name,
        collection.collection_name,
        collection.internal_name,
        collection.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [collections, collectionSearch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Show warning if no collections exist
  if (collections.length === 0 && !resolveCollection) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
          <p className="mt-1 text-sm text-gray-600">Add documents your agent can answer from</p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-yellow-900 mb-2">Nowhere to add documents yet</h2>
          <p className="text-yellow-800 mb-6">
            Your agent&rsquo;s knowledge is set up the first time you add business knowledge.
          </p>
          <button
            onClick={() => navigate(tenantId ? `/t/${tenantId}/rag/sources` : '/platform/rag/sources')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Open business knowledge
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {/* Header */}
      {!embedded && <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
        <p className="mt-1 text-sm text-gray-600">Add documents your agent can answer from</p>
      </div>}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Silent-failure guard: the upload "succeeded" but produced no/too-little
          searchable content (e.g. a scanned or password-protected PDF). */}
      {warning && (
        <div className={`px-4 py-3 rounded relative border ${
          warning.verdict === 'empty'
            ? 'bg-red-50 border-red-300 text-red-800'
            : 'bg-amber-50 border-amber-300 text-amber-800'
        }`}>
          <p className="font-semibold">
            {warning.verdict === 'empty'
              ? 'This file added no searchable content.'
              : 'This file added very little content.'}
          </p>
          <p className="text-sm mt-1">{warning.reason}</p>
          {warning.recommendation && (
            <p className="text-sm mt-1 font-medium">{warning.recommendation}</p>
          )}
          <button
            onClick={() => setWarning(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Content indexed and searchable, but its durable backup didn't
          activate — distinct from the silent-failure guard above (which means
          the upload itself produced little/no content). Non-blocking. */}
      {backupWarning && (
        <div className="px-4 py-3 rounded relative border bg-amber-50 border-amber-300 text-amber-800">
          <p className="text-sm">{backupWarning}</p>
          <button
            onClick={() => setBackupWarning(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Upload Form */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <form onSubmit={handleUpload} className="space-y-6">
          {/* Target Collection — explicit selection required */}
          {!embedded && <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🎯 Target Collection <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Select the collection where uploaded documents will be embedded and stored.
            </p>

            <div className="mb-3">
              <input
                type="search"
                value={collectionSearch}
                onChange={(e) => setCollectionSearch(e.target.value)}
                placeholder="Search collections by name, internal name, or description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Showing {filteredCollections.length} of {collections.length} collections.
              </p>
            </div>

            {!collectionId && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                <span className="text-amber-500 text-lg">⚠️</span>
                <p className="text-sm text-amber-700 font-medium">
                  You must select a target collection before uploading.
                </p>
              </div>
            )}

            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {collections.length === 0 ? (
                <p className="p-4 text-sm text-gray-400 text-center">No collections available</p>
              ) : filteredCollections.length === 0 ? (
                <p className="p-4 text-sm text-gray-400 text-center">No collections match your search</p>
              ) : (
                filteredCollections.map((collection) => {
                  const optionId = String(collection.id);
                  const optionName = collection.name || collection.collection_name;
                  const isSelected = collectionId === optionId;
                  return (
                    <label
                      key={collection.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-indigo-50 border-l-4 border-indigo-500'
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      <input
                        type="radio"
                        name="targetCollection"
                        value={optionId}
                        checked={isSelected}
                        onChange={() => setCollectionId(optionId)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-gray-700'}`}>
                          {optionName}
                        </p>
                        <p className="text-xs text-gray-400">{collection.documents_count || 0} docs</p>
                      </div>
                      {isSelected && <span className="text-indigo-500 text-lg">✅</span>}
                    </label>
                  );
                })
              )}
            </div>
          </div>}

          {/* File Drop Zone */}
          <div
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                setFile(e.dataTransfer.files[0]);
              }
            }}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {file ? (
              <div className="space-y-4">
                <div className="text-6xl">📄</div>
                <div>
                  <p className="text-lg font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500 mt-1">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-6xl">📁</div>
                <div>
                  {embedded && (
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-violet-800">Choose your file</div>
                  )}
                  <p className="text-lg font-medium text-gray-900">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-gray-500 mt-1">or</p>
                </div>
                <label className="cursor-pointer rounded-lg focus-within:outline-none focus-within:ring-4 focus-within:ring-violet-200">
                  <span className={`inline-block rounded-lg px-4 py-2 text-sm font-medium ${
                    embedded
                      ? 'border-2 border-violet-500 bg-violet-50 font-bold text-violet-900 hover:bg-violet-100'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}>
                    Browse Files
                  </span>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                    // sr-only rather than hidden: a display:none input cannot be
                    // focused, so the wizard could not put the cursor on the one
                    // control this step is asking the customer to use.
                    autoFocus={embedded}
                    className={embedded ? 'sr-only' : 'hidden'}
                    accept=".pdf,.docx,.txt,.md,.csv,.html,.htm,.json,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.doc,.xls,.xlsx,.ppt,.pptx,.rtf,.odt"
                  />
                </label>
                <p className="text-xs text-gray-500">
                  Supported: PDF, DOCX, TXT, MD, HTML, CSV, JSON, PNG, JPG, WebP &nbsp;·&nbsp; Legacy formats (DOC, XLSX, PPTX) show conversion instructions on upload.
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  <strong>Limits:</strong> up to <strong>50 MB</strong> and <strong>300 pages</strong> per file. Each page of a file counts toward the knowledge pages your plan includes. For most accurate answers use text-based PDFs — scanned pages are processed as image summaries, which capture the gist but not exact text or numbers.
                </p>
              </div>
            )}
          </div>

          {/* Tips for best results — expandable so it doesn't intimidate first-time uploaders */}
          {!embedded && !file && (
            <details className="mt-6 rounded-lg border border-blue-200 bg-blue-50">
              <summary className="cursor-pointer px-4 py-3 flex items-center gap-2 text-sm font-semibold text-blue-900 hover:bg-blue-100 rounded-lg">
                <LightBulbIcon className="w-5 h-5 text-blue-600" />
                Tips for the sharpest answers from your AI
                <span className="ml-auto text-xs font-normal text-blue-600">tap to expand</span>
              </summary>
              <div className="px-4 pb-4 pt-2 text-sm text-gray-700 space-y-3 border-t border-blue-200">
                <div>
                  <p className="font-semibold text-gray-900">Best formats</p>
                  <p className="text-gray-600 mt-1">
                    <strong>Markdown (.md)</strong> gives the sharpest results because we split on section headers, so
                    the AI finds the right passage first. <strong>Plain text (.txt)</strong> and clean{' '}
                    <strong>Word (.docx)</strong> are close behind. <strong>PDFs</strong> work but scanned PDFs are weaker &mdash;
                    convert to Markdown if you can.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">What to upload</p>
                  <p className="text-gray-600 mt-1">
                    Brochures, price lists, service catalogues, admission handbooks, FAQs &mdash; whatever your customers
                    actually ask about. Skip internal docs (sales scripts, HR handbooks) &mdash; the AI will accidentally
                    quote them.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Focused beats big</p>
                  <p className="text-gray-600 mt-1">
                    20 well-organised documents outperform 200 miscellaneous ones. If prices or policies change,
                    re-upload the updated document &mdash; the AI answers from what&rsquo;s here right now.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">The one thing that helps most</p>
                  <p className="text-gray-600 mt-1">
                    Author a single <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">top-questions.md</code>{' '}
                    file with your top 20 customer questions and answers, each under its own <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">##</code>{' '}
                    heading. That one file typically covers 60&ndash;80% of everything a visitor asks. Everything else
                    you upload becomes depth.
                  </p>
                </div>
                <div className="pt-2 border-t border-blue-100">
                  <a
                    href="https://serviti.ai/docs/best-answers"
                    target="_blank"
                    rel="noopener"
                    className="text-blue-700 hover:text-blue-900 font-medium text-sm inline-flex items-center gap-1"
                  >
                    Read the full accuracy guide &rarr;
                  </a>
                  <span className="text-xs text-gray-500 ml-2">
                    or ask <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">support@serviti.ai</code>
                  </span>
                </div>
              </div>
            </details>
          )}

          {/* Upload Progress Bar */}
          {uploading && (
            <div className="w-full">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload/Stop Buttons */}
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={uploading || !file || (!collectionId && !resolveCollection)}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <span>⬆️</span>
                  <span>Upload Document</span>
                </>
              )}
            </button>
            
            {uploading && (
              <button
                type="button"
                onClick={() => {
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                  }
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
              >
                <span>⏹️</span>
                <span>Stop</span>
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">📝 Upload Instructions</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Select a collection where you want to store the document</li>
          <li>Drag and drop a file or click "Browse Files" to select</li>
          <li>Supported formats: PDF, Word documents, text files, Markdown, CSV, and HTML</li>
          <li>Maximum file size: 50 MB</li>
          <li>The document will be automatically processed and indexed into the selected collection</li>
        </ul>
      </div>
    </div>
  );
};

export default Upload;
